import React, {ReactElement, ReactNode} from 'react';
import Sidebar, {Props as SidebarProps} from './Sidebar/Sidebar';
import {useDevice, Device} from '../../../../RootProvider/DeviceContext';
import PartnerLogo, {Props as PartnerLogoProps} from '../../PartnerLogo/PartnerLogo';
import LayoutSectionCircles, {LayoutCircleConfig} from './LayoutSectionCircles';
import {Alignment} from '../../../../types';
import './LayoutSection.scss';

export enum LayoutBackground {
  WHITE = 'white',
  GRAY = 'gray',
  ACCENT = 'accent',
  BLUE = 'blue',
}

export enum SidebarVariant {
  DARK = '--sidebarVariantDark',
  LIGHT = '--sidebarVariantLight',
}

interface SidebarPropsExtended extends Omit<SidebarProps, 'partner'> {
  sidebarVariant?: SidebarVariant;
}

interface Props {
  children?: ReactNode;
  circles?: LayoutCircleConfig;
  className?: string;
  innerAlignment?: Alignment.LEFT | Alignment.CENTER;
  innerClassName?: string;
  partner?: PartnerLogoProps;
  sidebar?: SidebarPropsExtended;
  slim?: boolean;
  type?: LayoutBackground;
}

function LayoutSection({
  type,
  innerClassName,
  className,
  children,
  circles,
  slim,
  sidebar,
  partner,
  innerAlignment = Alignment.LEFT,
}: Props): ReactElement {
  const isDesktop = useDevice() === Device.DESKTOP;
  // As the element class attribution changes depending on the LIGHT/DARK variants
  // the functions that determine if it's dark or light are separate
  // i.e. the dark variant gets applied to the most outer element
  // and the light variant gets applied to the sidebar
  const getDarkVariant = (): string => {
    if (sidebar?.sidebarVariant === SidebarVariant.DARK) {
      return 'layoutSection__innerRight--sidebarVariantDark';
    }
    return '';
  };

  const getLightVariant = (): string => {
    if (!sidebar?.sidebarVariant) {
      return 'layoutSection__innerRight--sidebarVariantLight';
    }
    if (sidebar?.sidebarVariant === SidebarVariant.LIGHT) {
      return 'layoutSection__innerRight--sidebarVariantLight';
    }
    return '';
  };

  const getAlignment = () => {
    if (innerAlignment === Alignment.LEFT) {
      return 'innerLeft';
    }

    return 'innerCenter';
  };

  return (
    <section
      className={`layoutSection ${type ? `layoutSection--${type}` : ''} ${className || ''}`
        .replace(/\s{2}/g, ' ')
        .trim()}
      data-testid="ui.layoutSection"
    >
      <div
        className={`layoutSection__${getAlignment()} ${slim ? `layoutSection__${getAlignment()}--slim` : ''} ${
          sidebar ? `layoutSection__${getAlignment()}--withSidebar` : ''
        }`
          .replace(/\s{2}/g, ' ')
          .trim()}
      >
        {circles && <LayoutSectionCircles config={circles} />}
        <div
          className={`layoutSection__${getAlignment()}__content ${innerClassName || ''} ${
            sidebar ? `layoutSection__${getAlignment()}__content--withSidebar` : ''
          }`.trim()}
        >
          {!isDesktop && partner && (
            <div className={`layoutSection__${getAlignment()}__content__partnerHeader`}>
              <PartnerLogo name={partner.name} logoUrl={partner.logoUrl} />
            </div>
          )}
          {children}
        </div>
      </div>
      {sidebar && (
        <div
          className={`layoutSection__innerRight ${innerClassName || ''} ${
            slim ? 'layoutSection__innerRight--slim' : ''
          } ${getDarkVariant()}`
            .replace(/\s{2}/g, ' ')
            .trim()}
        >
          <div className={`layoutSection__innerRight__sidebar ${getLightVariant()}`}>
            <Sidebar
              advantages={sidebar.advantages}
              hideAdvantages={sidebar.hideAdvantages}
              partner={partner}
              sideBarBottomComponent={sidebar.sideBarBottomComponent}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export default LayoutSection;
